import { computed, makeObservable } from 'mobx'
import type { SlideDeckExhibit } from './SlideDeckExhibit'
import type { MeetingCubit } from '../cubits/MeetingCubit'
import type { SlideQuestion } from './SlideQuestion'
import type { RoomStateAnswer } from './RoomStateAnswer'
import { SlideQuestionType } from './SlideQuestionType'
import Handlebars from 'handlebars'
import type { MarkdownInputs } from '../firestore/SlideDeckExhibit/types'
import type {
  DynamicExhibitItem,
  InputSource,
} from '../firestore/SlideDeckExhibit/types'

export { InputSource }

/**
 * Wraps a SlideDeckExhibit with meeting-specific computed properties.
 * Delegates all base exhibit properties to the underlying SlideDeckExhibit
 * and adds `computedMarkdownContent` which resolves dynamic Handlebars
 * templates with live answer data from the meeting.
 */
export class MeetingExhibit {
  readonly exhibit: SlideDeckExhibit
  private meeting: MeetingCubit

  constructor(exhibit: SlideDeckExhibit, meeting: MeetingCubit) {
    this.exhibit = exhibit
    this.meeting = meeting

    makeObservable(this)
  }

  // Delegated properties
  get id() {
    return this.exhibit.id
  }

  get data() {
    return this.exhibit.data
  }

  get altText() {
    return this.exhibit.altText
  }

  get inputSources() {
    return this.exhibit.inputSources
  }

  get hasInputSources() {
    return this.exhibit.hasInputSources
  }

  get media() {
    return this.exhibit.media
  }

  get hasMedia() {
    return this.exhibit.hasMedia
  }

  loadMedia() {
    return this.exhibit.loadMedia()
  }

  @computed
  get computedMarkdownContent(): string {
    const markdown = this.data.exhibitMarkdown
    if (!markdown) return ''

    if (!this.hasInputSources) {
      return markdown
    }

    if (this.meeting.isPreview) {
      return MeetingExhibit.compileMarkdown(
        markdown,
        MeetingExhibit.createPreviewInputs(this.exhibit.inputSources)
      )
    }

    // Resolve live answer data
    const inputs = this.resolveMarkdownInputs
    return MeetingExhibit.compileMarkdown(markdown, inputs)
  }

  @computed
  get resolveMarkdownInputs() {
    const inputs: MarkdownInputs = {}

    for (const source of this.inputSources) {
      const { questionId, variableName, representationType } = source

      const question = this.meeting.slideDeckQuestions.models.find(
        (q) => q.id === questionId
      )
      if (!question) continue

      let items: string | DynamicExhibitItem[]

      if (representationType === 'text') {
        items = question.data.question || ''
      } else if (representationType === 'group') {
        items = this.resolveAnswerItems(
          question,
          this.meeting.roomStateAnswersForGroup.models
        )
      } else {
        // individual
        const col = this.meeting.roomStateAnswersPerUser.get(
          this.meeting.currentUser.uid
        )
        items = this.resolveAnswerItems(question, col?.models ?? [])
      }

      if (typeof items === 'string') {
        inputs[variableName] = items
      } else {
        const existing = inputs[variableName]
        if (Array.isArray(existing)) {
          inputs[variableName] = [...existing, ...items]
        } else {
          inputs[variableName] = items
        }
      }
    }

    return inputs
  }

  private resolveAnswerItems(
    question: SlideQuestion,
    answers: RoomStateAnswer[]
  ): DynamicExhibitItem[] {
    const questionType = question.questionType
    const relevantAnswers = answers.filter(
      (a) => a.data.slideQuestionId === question.id
    )

    if (relevantAnswers.length === 0) return []

    const items: DynamicExhibitItem[] = []

    for (const answer of relevantAnswers) {
      if (
        questionType === SlideQuestionType.multipleChoice ||
        questionType === SlideQuestionType.poll ||
        questionType === SlideQuestionType.customPoll
      ) {
        const answerIndex = answer.data.answer
        const answerText = question.data.answers?.[answerIndex]
        if (answerText) {
          items.push({ item: answerText })
        }
      } else if (questionType === SlideQuestionType.sorting) {
        const answerList = answer.data.answerList
        if (answerList) {
          for (const idx of answerList) {
            const text = question.data.answers?.[idx]
            if (text) {
              items.push({ item: text })
            }
          }
        }
      } else if (
        questionType === SlideQuestionType.sortableStringList ||
        questionType === SlideQuestionType.interactiveFormElement
      ) {
        const answerObjects = answer.data.answerObjects
        if (answerObjects) {
          const sorted = Object.values(answerObjects).sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0)
          )
          for (const obj of sorted) {
            if (obj.text) {
              items.push({ item: obj.text })
            }
          }
        }
      }
    }

    return items
  }

  static titleizeVariableName(variableName: string): string {
    // Convert camelCase or snake_case to Title Case
    const words = variableName
      .replace(/([A-Z])/g, ' $1') // camelCase to spaces
      .replace(/_/g, ' ') // snake_case to spaces
      .trim()
      .split(' ')

    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  /**
   * Creates preview markdownInputs with fake data for all input sources.
   */
  static createPreviewInputs(inputSources: InputSource[]): MarkdownInputs {
    const inputs: MarkdownInputs = {}

    for (const source of inputSources) {
      const { variableName, representationType } = source

      if (representationType === 'text') {
        inputs[variableName] = MeetingExhibit.titleizeVariableName(variableName)
      } else {
        const existing = inputs[variableName]
        if (Array.isArray(existing)) {
          // Already populated by another source with the same variable name
          continue
        }
        inputs[variableName] = [1, 2, 3].map((n) => ({
          item: `${MeetingExhibit.titleizeVariableName(variableName)} ${n}`,
        }))
      }
    }

    return inputs
  }

  /**
   * Compiles markdown with the given inputs, escaping HTML in values.
   */
  static compileMarkdown(markdown: string, inputs: MarkdownInputs): string {
    if (!markdown) return ''

    try {
      const escapedInputs: MarkdownInputs = {}
      for (const [key, value] of Object.entries(inputs)) {
        if (typeof value === 'string') {
          escapedInputs[key] = Handlebars.Utils.escapeExpression(value)
        } else {
          escapedInputs[key] = value.map((v) => ({
            item: Handlebars.Utils.escapeExpression(v.item),
          }))
        }
      }

      const template = Handlebars.compile(markdown, { noEscape: true })
      return template(escapedInputs)
    } catch {
      return markdown
    }
  }
}
